import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Banner, Button, cn } from '@beatq/ui';
import { endImpersonationSession } from '../features/entity-360/entity360Api.js';
import {
  getStoredImpersonationSession,
  markImpersonationEnded,
  setStoredImpersonationSession,
} from '../features/entity-360/entity360State.js';
import { AdminIcon, Clock, Eye, ShieldAlert, UserRound } from './adminIcons.js';
import {
  buildEndedGuard,
  formatConsentClock,
  formatCountdown,
  getImpersonationBanner,
  IMPERSONATION_CHANGED_EVENT,
  isTwoMinuteWarning,
  recordImpersonationVisit,
  remainingSeconds,
  setImpersonationBanner,
  setImpersonationEndedGuard,
  tenantViewFromLocation,
  type ImpersonationBannerState,
  type ImpersonationIntensity,
} from './adminSession.js';
import { caseDetailPath } from './subjectUrls.js';

const INTENSITY_COPY: Record<
  ImpersonationIntensity,
  { kicker: string; bars: string; exit: string; hint: string }
> = {
  'tenant-view': {
    kicker: 'VIEWING TENANT',
    bars: '▮',
    exit: 'Leave',
    hint: 'Read-only. All access recorded.',
  },
  'impersonate-read': {
    kicker: 'IMPERSONATING',
    bars: '▮▮',
    exit: 'End now',
    hint: 'Read-only.',
  },
  'impersonate-write': {
    kicker: 'IMPERSONATING WITH WRITE',
    bars: '▮▮▮',
    exit: 'End now',
    hint: 'Each change requires a reason.',
  },
};

function intensityIcon(intensity: ImpersonationIntensity) {
  if (intensity === 'tenant-view') return Eye;
  if (intensity === 'impersonate-write') return ShieldAlert;
  return UserRound;
}

function intensityClass(intensity: ImpersonationIntensity): string {
  if (intensity === 'impersonate-write') return 'a01-banner a01-banner--write';
  if (intensity === 'impersonate-read') return 'a01-banner a01-banner--read';
  return 'a01-banner a01-banner--tenant';
}

export function resolveBannerState(
  pathname: string,
  searchParams: URLSearchParams,
): ImpersonationBannerState | null {
  const stored = getStoredImpersonationSession();
  const banner = getImpersonationBanner();
  if (stored?.sessionId) {
    const write = stored.writeEnabled === true || stored.scope === 'write';
    const caseId = searchParams.get('caseId') ?? stored.caseId ?? stored.caseRef ?? banner?.caseId;
    return {
      intensity: write ? 'impersonate-write' : (banner?.intensity ?? 'impersonate-read'),
      sessionId: stored.sessionId,
      subjectName: stored.subjectName ?? banner?.subjectName ?? stored.subjectId ?? 'Subject',
      subjectId: stored.subjectId ?? banner?.subjectId ?? stored.sessionId,
      caseId,
      caseHref: caseId ? caseDetailPath(caseId) : banner?.caseHref,
      expiresAt: stored.expiresAt ?? banner?.expiresAt,
      consentAt: banner?.consentAt ?? formatConsentClock(stored.consentRecordedAt),
    };
  }
  if (banner?.sessionId) return banner;
  return tenantViewFromLocation(pathname, searchParams);
}

export async function exitImpersonationOrTenant(
  state: ImpersonationBannerState,
  opts?: { expired?: boolean },
): Promise<string> {
  const returnHref =
    state.caseHref ?? (state.caseId ? caseDetailPath(state.caseId) : '/admin-dashboard');
  setImpersonationEndedGuard(buildEndedGuard(returnHref, opts?.expired ? 'expired' : undefined));
  if (state.sessionId) {
    try {
      await endImpersonationSession(state.sessionId);
    } catch {
      /* clear local even if API fails */
    }
    markImpersonationEnded({
      sessionId: state.sessionId,
      caseId: state.caseId,
      subjectId: state.subjectId,
      endedAt: new Date().toISOString(),
    });
    setStoredImpersonationSession(null);
  }
  setImpersonationBanner(null);
  return returnHref;
}

export function TenantImpersonationBanner({
  pathname,
  searchParams,
}: {
  pathname: string;
  searchParams: URLSearchParams;
}) {
  const [now, setNow] = useState(() => Date.now());
  const state = useMemo(
    () => resolveBannerState(pathname, searchParams),
    [pathname, searchParams, now],
  );

  useEffect(() => {
    const tick = () => setNow(Date.now());
    const id = window.setInterval(tick, 1000);
    window.addEventListener(IMPERSONATION_CHANGED_EVENT, tick);
    window.addEventListener('storage', tick);
    return () => {
      window.clearInterval(id);
      window.removeEventListener(IMPERSONATION_CHANGED_EVENT, tick);
      window.removeEventListener('storage', tick);
    };
  }, []);

  useEffect(() => {
    if (state?.sessionId) recordImpersonationVisit(pathname);
  }, [state?.sessionId, pathname]);

  useEffect(() => {
    if (!state?.expiresAt || !state.sessionId) return;
    if (state.intensity === 'tenant-view') return;
    if (remainingSeconds(state.expiresAt, now) > 0) return;
    void exitImpersonationOrTenant(state, { expired: true }).then((href) => {
      window.location.replace(href);
    });
  }, [now, state]);

  if (!state) return null;
  const banner = state;

  const copy = INTENSITY_COPY[banner.intensity];
  const remaining =
    banner.intensity === 'tenant-view' ? null : remainingSeconds(banner.expiresAt, now);
  const warning = remaining != null && isTwoMinuteWarning(remaining);

  async function onExit() {
    const href = await exitImpersonationOrTenant(banner);
    window.location.replace(href);
  }

  const caseLabel = banner.caseId ? `case ${banner.caseId}` : null;

  return (
    <Banner
      label={copy.kicker}
      tone="impersonation"
      className={cn(intensityClass(banner.intensity), warning && 'a01-banner--warn')}
    >
      <span className="flex flex-wrap items-center gap-field">
        <AdminIcon icon={intensityIcon(banner.intensity)} size={18} label={copy.kicker} />
        <span className="a01-banner__bars" aria-hidden>
          {copy.bars}
        </span>
        <strong className="a01-banner__kicker">{copy.kicker}</strong>
        <span>
          {banner.subjectName}
          {banner.subjectId && banner.subjectId !== banner.subjectName
            ? ` · ${banner.subjectId}`
            : null}
        </span>
        {caseLabel ? (
          banner.caseHref ? (
            <Link to={banner.caseHref} className="underline">
              {caseLabel}
            </Link>
          ) : (
            <span>{caseLabel}</span>
          )
        ) : null}
        {banner.consentAt && banner.intensity !== 'tenant-view' ? (
          <span>consent recorded {banner.consentAt}</span>
        ) : null}
        {remaining != null ? (
          <span className="inline-flex items-center gap-xs">
            <AdminIcon icon={Clock} size={14} />
            <span>
              {formatCountdown(remaining)} remaining
              {warning ? ': two minutes left' : null}
            </span>
          </span>
        ) : null}
        <span className="text-body-sm">{copy.hint}</span>
        <Button type="button" variant="secondary" onClick={() => void onExit()}>
          {copy.exit}
        </Button>
      </span>
    </Banner>
  );
}
